import * as React from 'react';

import { Row } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/field';
import { cn } from '@/lib/utils';

export type InputProps = React.ComponentProps<'input'> & {
    error?: string;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    startIconLabel?: string;
    endIconLabel?: string;
    startIconIsButton?: boolean;
    endIconIsButton?: boolean;
    onStartIconButtonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onEndIconButtonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type,
            error,
            startIcon,
            endIcon,
            startIconLabel,
            endIconLabel,
            startIconIsButton,
            endIconIsButton,
            onStartIconButtonClick,
            onEndIconButtonClick,
            ...props
        },
        ref,
    ) => {
        return (
            <>
                <Row className={cn('relative h-10 w-full gap-2', className)}>
                    {startIcon && (
                        <>
                            {startIconIsButton ? (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-muted-foreground focus-visible:ring-ring/50 absolute top-1/2 left-0 -translate-y-1/2 rounded-r-none hover:bg-transparent"
                                    icon={startIcon}
                                    onClick={onStartIconButtonClick}
                                    aria-label={startIconLabel}
                                />
                            ) : (
                                <div
                                    aria-label={startIconLabel}
                                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-0 flex -translate-y-1/2 items-center justify-center p-2 peer-disabled:opacity-50"
                                >
                                    {startIcon}
                                </div>
                            )}
                        </>
                    )}
                    <input
                        ref={ref}
                        type={type}
                        data-slot="input"
                        className={cn(
                            'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input desktop:text-sm h-9 w-full min-w-0 rounded-xs border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
                            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                            'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
                            'h-full grow',
                            startIcon && 'pl-8!',
                            endIcon && 'pr-8!',
                        )}
                        aria-invalid={!!error}
                        {...props}
                    />
                    {endIcon && (
                        <>
                            {endIconIsButton ? (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-muted-foreground focus-visible:ring-ring/50 absolute top-1/2 right-0 -translate-y-1/2 rounded-l-none hover:bg-transparent"
                                    icon={endIcon}
                                    onClick={onEndIconButtonClick}
                                    aria-label={endIconLabel}
                                />
                            ) : (
                                <div
                                    aria-label={endIconLabel}
                                    className="text-muted-foreground pointer-events-none absolute top-1/2 right-0 flex -translate-y-1/2 items-center justify-center pr-3 peer-disabled:opacity-50"
                                >
                                    {endIcon}
                                </div>
                            )}
                        </>
                    )}
                </Row>

                {error && <FieldError errors={[{ message: error }]} />}
            </>
        );
    },
);

Input.displayName = 'Input';

export { Input };
